(function () {
  const PLAN_ORDER = { basic: 0, pro: 1, prime: 2 };
  const FEATURE_DIVIDER = '__divider__';
  const CHECKOUT_ID_STORAGE_KEY = 'jjb_membership_checkout_id';
  const CHECKOUT_SOURCE_STORAGE_KEY_PREFIX = 'jjb_membership_checkout_source_';
  const VIP_BANNER_EXPERIMENT = 'show_vip_banner';
  const VIP_BANNER_EXPERIMENT_GROUP_IDS = { false: 11, true: 12 };
  const PLANS = [
    {
      plan_type: 'basic',
      monthly_price: 0,
      yearly_original_price: 0,
      yearly_first_year_price: 0,
    },
    {
      plan_type: 'pro',
      monthly_price: 19.9,
      yearly_original_price: 238.8,
      yearly_first_year_price: 191.04,
    },
    {
      plan_type: 'prime',
      monthly_price: 99.9,
      yearly_original_price: 1198.8,
      yearly_first_year_price: 959.04,
    },
  ];
  const paywallStates = new WeakMap();
  const membershipRefreshes = new WeakMap();
  const membershipDefaultNavigation = new WeakMap();
  const pageScrollLockOwners = new Set();
  let autoCheckoutHandled = false;
  let pageScrollLockSnapshot = null;

  function isTestMode() {
    let shopMode = '';
    try {
      shopMode = window.localStorage.getItem('_shop_mode') || '';
    } catch (_) {}
    return (
      window.location.hostname.includes('localhost') ||
      window.location.hostname.includes('127.0.0.1') ||
      window.location.hostname.includes('shopifypreview.com') ||
      shopMode === 'test'
    );
  }

  function getMembershipApiPrefix() {
    const baseUrl = isTestMode() ? '/apps/ai-test' : '/apps/ai';
    return baseUrl + '/membership';
  }

  function getExperiment(name) {
    let request;
    if (isTestMode() && window.jjbUtils?.overrideExperiment) {
      request = window.jjbUtils.overrideExperiment(name);
    } else if (window.statsigReady && window.statsigClient) {
      request = window.statsigReady.then(() =>
        window.statsigClient.getExperiment(name),
      );
    } else {
      request = Promise.resolve(null);
    }
    return Promise.race([
      request,
      new Promise((resolve) => window.setTimeout(() => resolve(null), 2000)),
    ]);
  }

  function withTestBypass(path) {
    if (!isTestMode()) return path;
    const parts = String(path || '').split('#');
    const pathname = parts[0];
    const hash = parts.length > 1 ? parts.slice(1).join('#') : '';
    const queryIndex = pathname.indexOf('?');
    const basePath =
      queryIndex === -1 ? pathname : pathname.slice(0, queryIndex);
    const queryString = queryIndex === -1 ? '' : pathname.slice(queryIndex + 1);
    const params = new URLSearchParams(queryString);
    const query = params.toString();
    const nextPath = query ? basePath + '?' + query : basePath;
    return hash ? nextPath + '#' + hash : nextPath;
  }

  function getIdempotencyKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return (
      'membership-' +
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2)
    );
  }

  function getErrorMessage(error) {
    if (error && error.message) return error.message;
    return 'Something went wrong. Please try again.';
  }

  function showMembershipRequestError(error) {
    if (typeof window.toast !== 'function') return;
    if (error?.status === 400 && String(error.code) === '1027') {
      window.toast(
        'Your membership information is syncing. Please refresh the page.',
        10000,
      );
      return;
    }
    if (error?.status === 429) {
      window.toast(
        'Please refresh the page and complete the human verification process.',
        10000,
      );
      return;
    }
    if (error?.status >= 500 && error.status <= 599) {
      window.toast(
        `${getErrorMessage(error)} Please take a screenshot and contact customer support.`,
        10000,
      );
      return;
    }
    window.toast(getErrorMessage(error));
  }

  async function requestMembershipApi(path, options) {
    const requestOptions = options || {};
    const isFormData =
      typeof FormData !== 'undefined' &&
      requestOptions.body instanceof FormData;
    const headers = Object.assign(
      { Accept: 'application/json' },
      requestOptions.body && !isFormData
        ? { 'Content-Type': 'application/json' }
        : {},
      requestOptions.headers || {},
    );
    const response = await fetch(
      getMembershipApiPrefix() + withTestBypass(path),
      {
        method: requestOptions.method || 'GET',
        headers,
        body:
          requestOptions.body &&
          !isFormData &&
          typeof requestOptions.body !== 'string'
            ? JSON.stringify(requestOptions.body)
            : requestOptions.body,
        credentials: 'same-origin',
      },
    );
    let payload = null;
    try {
      payload = await response.json();
    } catch (_) {}
    if (!response.ok || !payload || payload.success === false) {
      const error = new Error(
        (payload && (payload.message || payload.errorMessage)) ||
          'Membership request failed.',
      );
      error.code = payload && payload.code;
      error.status = response.status;
      throw error;
    }
    return payload.data;
  }

  function toBillingCycle(cycle) {
    return cycle === 'yearly' ? 'YEAR' : 'MONTH';
  }

  function normalizeBillingCycle(cycle, planType) {
    if (normalizePlanType(planType) === 'basic') return 'free';
    const value = String(cycle || '').toUpperCase();
    return value === 'YEAR' || value === 'YEARLY' ? 'yearly' : 'monthly';
  }

  function toPlanType(plan) {
    const value = String(plan || '').toUpperCase();
    if (value === 'PREMIUM') return 'prime';
    if (value === 'PRO') return 'pro';
    return 'basic';
  }

  function normalizeMembershipStatus(status) {
    const value = String(status || 'BASIC').toUpperCase();
    const statuses = [
      'BASIC',
      'PROCESSING',
      'ACTIVE',
      'CANCELLING',
      'DUNNING',
      'SUSPENDED',
      'EXPIRED',
    ];
    return statuses.indexOf(value) === -1 ? 'BASIC' : value;
  }

  function isFutureTime(value) {
    if (!value) return false;
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) && timestamp > Date.now();
  }

  function getOptionalBoolean() {
    for (let index = 0; index < arguments.length; index += 1) {
      const value = arguments[index];
      const normalizedValue =
        typeof value === 'string' ? value.trim().toLowerCase() : value;
      if (
        normalizedValue === true ||
        normalizedValue === 'true' ||
        normalizedValue === 1 ||
        normalizedValue === '1'
      ) {
        return true;
      }
      if (
        normalizedValue === false ||
        normalizedValue === 'false' ||
        normalizedValue === 0 ||
        normalizedValue === '0'
      ) {
        return false;
      }
    }
    return null;
  }

  function normalizeMembershipState(state) {
    if (!state || typeof state !== 'object') {
      return {
        plan: 'BASIC',
        status: 'BASIC',
        paidThrough: '',
        billingCycle: '',
        autoRenew: false,
        autoRenewSpecified: false,
        cancelAtPeriodEnd: false,
        membershipSource: 'PAID',
        giftId: null,
        giftEndsAt: null,
        resumeVisible: false,
        renewalPlan: null,
        renewalBillingCycle: null,
      };
    }
    const nestedState =
      state.membership && typeof state.membership === 'object'
        ? state.membership
        : state.subscription && typeof state.subscription === 'object'
          ? state.subscription
          : state.membershipState && typeof state.membershipState === 'object'
            ? state.membershipState
            : state;
    const status = normalizeMembershipStatus(
      nestedState.membershipStatus ||
        nestedState.status ||
        nestedState.membership_status ||
        nestedState.subscriptionStatus ||
        nestedState.subscription_status,
    );
    const autoRenew = getOptionalBoolean(
      nestedState.autoRenew,
      nestedState.auto_renew,
      nestedState.autoRenewal,
      nestedState.auto_renewal,
    );
    const autoRenewSpecified =
      typeof state.autoRenewSpecified === 'boolean'
        ? state.autoRenewSpecified
        : autoRenew !== null;
    const explicitCancelAtPeriodEnd = getOptionalBoolean(
      nestedState.cancelAtPeriodEnd,
      nestedState.cancel_at_period_end,
      nestedState.cancelledAtPeriodEnd,
      nestedState.cancelled_at_period_end,
      nestedState.willCancelAtPeriodEnd,
      nestedState.will_cancel_at_period_end,
    );
    return {
      plan: String(
        nestedState.plan ||
          nestedState.plan_type ||
          nestedState.tier ||
          'BASIC',
      ).toUpperCase(),
      status,
      billingStatus: String(nestedState.billingStatus || '').toUpperCase(),
      paidThrough:
        nestedState.paidThrough ||
        nestedState.currentPeriodEndsAt ||
        nestedState.periodEndAt ||
        nestedState.period_end_at ||
        nestedState.paid_through ||
        '',
      billingCycle: nestedState.billingCycle || nestedState.billing_cycle || '',
      membershipSource:
        String(
          nestedState.membershipSource ||
            nestedState.membership_source ||
            'PAID',
        ).toUpperCase() === 'GIFT'
          ? 'GIFT'
          : 'PAID',
      giftId: nestedState.giftId || nestedState.gift_id || null,
      giftEndsAt: nestedState.giftEndsAt || nestedState.gift_ends_at || null,
      resumeVisible: getOptionalBoolean(
        nestedState.resumeVisible,
        nestedState.resume_visible,
      ),
      renewalSignupId:
        nestedState.renewalSignupId || nestedState.renewal_signup_id || null,
      renewalStatus:
        nestedState.renewalStatus || nestedState.renewal_status || null,
      renewalPlan: nestedState.renewalPlan || nestedState.renewal_plan || null,
      renewalBillingCycle:
        nestedState.renewalBillingCycle ||
        nestedState.renewal_billing_cycle ||
        null,
      renewalScheduledAt:
        nestedState.renewalScheduledAt ||
        nestedState.renewal_scheduled_at ||
        null,
      autoRenew: autoRenew === true,
      autoRenewSpecified,
      cancelAtPeriodEnd:
        status === 'CANCELLING' ||
        explicitCancelAtPeriodEnd === true ||
        (autoRenewSpecified && autoRenew === false),
    };
  }

  function requiresPaymentMethodUpdate(state) {
    const membership = normalizeMembershipState(state);
    return (
      membership.status === 'DUNNING' ||
      (membership.billingStatus === 'UNPAID' &&
        ['ACTIVE', 'CANCELLING'].includes(membership.status))
    );
  }

  function mapMembershipState(state) {
    const membershipState = normalizeMembershipState(state);
    const planType = toPlanType(membershipState.plan);
    const hasPaidPlan = planType !== 'basic';
    const hasActiveEntitlement =
      hasPaidPlan &&
      membershipState.membershipSource !== 'GIFT' &&
      (membershipState.status === 'ACTIVE' ||
        ((membershipState.status === 'CANCELLING' ||
          membershipState.status === 'DUNNING') &&
          isFutureTime(membershipState.paidThrough)));
    const hasActiveGift =
      membershipState.membershipSource === 'GIFT' &&
      membershipState.status === 'ACTIVE' &&
      (membershipState.giftEndsAt === null ||
        isFutureTime(membershipState.giftEndsAt));
    const giftResumeVisible =
      hasActiveGift &&
      !!membershipState.giftEndsAt &&
      membershipState.resumeVisible !== false &&
      !hasOpenGiftSignup(membershipState);
    const isCancelling =
      hasActiveEntitlement && membershipState.cancelAtPeriodEnd;
    return {
      plan_type: hasActiveEntitlement || hasActiveGift ? planType : 'basic',
      membership_status: membershipState.status,
      billing_status: membershipState.billingStatus,
      paid_through: membershipState.paidThrough,
      auto_renew: membershipState.autoRenew,
      cancel_at_period_end: isCancelling,
      billing_cycle: hasActiveEntitlement
        ? normalizeBillingCycle(membershipState.billingCycle, planType)
        : 'free',
      membership_source: membershipState.membershipSource,
      gift_id: membershipState.giftId,
      gift_ends_at: membershipState.giftEndsAt,
      resume_visible: giftResumeVisible,
      renewal_status: membershipState.renewalStatus,
      renewal_scheduled_at: membershipState.renewalScheduledAt,
      renewal_plan: membershipState.renewalPlan,
      renewal_billing_cycle: membershipState.renewalBillingCycle,
      vip_subscription_status: giftResumeVisible
        ? 'resume'
        : isCancelling
          ? 'cancel'
          : hasActiveEntitlement
            ? 'active'
            : '',
    };
  }

  function mapMembershipSummary(summary) {
    return mapMembershipState({
      ...summary,
      plan: summary && (summary.plan || summary.plan_type),
      status:
        summary &&
        (summary.status ||
          summary.membershipStatus ||
          summary.membership_status),
      paidThrough:
        summary &&
        (summary.paidThrough ||
          summary.currentPeriodEndsAt ||
          summary.periodEndAt ||
          summary.period_end_at ||
          summary.paid_through),
      billingCycle: summary && (summary.billingCycle || summary.billing_cycle),
      billingStatus:
        summary && (summary.billingStatus || summary.billing_status),
      autoRenew:
        summary &&
        (summary.autoRenew !== undefined
          ? summary.autoRenew
          : summary.auto_renew),
      cancelAtPeriodEnd:
        summary &&
        (summary.cancelAtPeriodEnd !== undefined
          ? summary.cancelAtPeriodEnd
          : summary.cancel_at_period_end),
    });
  }

  function hasOpenGiftSignup(state) {
    return [
      'PAYMENT_SOURCE_READY',
      'SETUP_PENDING',
      'SUBSCRIPTION_SCHEDULED',
      'SCHEDULED',
    ].includes(state.renewalStatus || state.renewal_status);
  }

  function canCancelMembershipPlan(membership) {
    const state = normalizeMembershipState(membership);
    const plan = state.plan === 'PRIME' ? 'PREMIUM' : state.plan;
    if (hasOpenGiftSignup(state)) return true;
    if (state.membershipSource === 'GIFT') return false;
    const autoRenewAllowsCancellation =
      !state.autoRenewSpecified || state.autoRenew === true;
    return (
      (plan === 'PRO' || plan === 'PREMIUM') &&
      state.status === 'ACTIVE' &&
      autoRenewAllowsCancellation &&
      state.cancelAtPeriodEnd === false
    );
  }

  function getThemeMembershipState() {
    if (window.theme && window.theme.membershipState) {
      return window.theme.membershipState;
    }
    if (window.JJB_CUSTOMER_MEMBERSHIP_STATE) {
      return window.JJB_CUSTOMER_MEMBERSHIP_STATE;
    }
    return {
      plan:
        (window.theme && window.theme.membershipPlan) ||
        window.JJB_CUSTOMER_MEMBERSHIP_PLAN ||
        'BASIC',
      status:
        (window.theme && window.theme.membershipStatus) ||
        window.JJB_CUSTOMER_MEMBERSHIP_STATUS ||
        'BASIC',
      paidThrough:
        (window.theme && window.theme.membershipPaidThrough) ||
        window.JJB_CUSTOMER_MEMBERSHIP_PAID_THROUGH ||
        '',
      billingCycle:
        (window.theme && window.theme.membershipBillingCycle) ||
        window.JJB_CUSTOMER_MEMBERSHIP_BILLING_CYCLE ||
        '',
      autoRenew:
        (window.theme && window.theme.membershipAutoRenew) ??
        window.JJB_CUSTOMER_MEMBERSHIP_AUTO_RENEW ??
        false,
      membershipSource:
        window.theme?.membershipSource ||
        window.JJB_CUSTOMER_MEMBERSHIP_SOURCE ||
        'PAID',
      giftId: window.theme?.giftId || window.JJB_CUSTOMER_GIFT_ID || null,
      giftEndsAt:
        window.theme?.giftEndsAt || window.JJB_CUSTOMER_GIFT_ENDS_AT || null,
      resumeVisible:
        window.theme?.resumeVisible === true ||
        window.JJB_CUSTOMER_RESUME_VISIBLE === true,
      cancelAtPeriodEnd:
        (window.theme && window.theme.membershipCancelAtPeriodEnd) ??
        window.JJB_CUSTOMER_MEMBERSHIP_CANCEL_AT_PERIOD_END ??
        false,
    };
  }

  function getCurrentMembership() {
    return mapMembershipState(getThemeMembershipState());
  }

  function isCustomerLoggedIn() {
    const customerId = window.theme && window.theme.customerId;
    return customerId !== undefined && customerId !== null && customerId !== '';
  }

  function bindMembershipProfile(root) {
    if (root.dataset.jjbMembershipProfileBound === 'true') return;
    root.dataset.jjbMembershipProfileBound = 'true';
    root
      .querySelectorAll('[data-membership-profile-open]')
      .forEach((button) => {
        button.addEventListener('click', () => {
          window.jjbMembershipProfilePopup?.open({
            firstName: String(window.theme?.customerFirstName || '').trim(),
            lastName: String(window.theme?.customerLastName || '').trim(),
            email: String(window.theme?.customerEmail || '').trim(),
          });
        });
      });
  }

  function getAuthUrl(returnUrl) {
    let target;
    try {
      const url = new URL(
        returnUrl || window.location.href,
        window.location.origin,
      );
      target =
        url.origin === window.location.origin
          ? url.pathname + url.search
          : window.location.pathname + window.location.search;
    } catch (_) {
      target = window.location.pathname + window.location.search;
    }
    return (
      '/customer_authentication/login?return_to=' + encodeURIComponent(target)
    );
  }

  function toCheckoutPlanParam(planType) {
    return normalizePlanType(planType) === 'prime' ? 'PREMIUM' : 'PRO';
  }

  function getCheckoutIntentUrl(planType, cycle) {
    const url = new URL(window.location.href);
    url.searchParams.set('plan', toCheckoutPlanParam(planType));
    url.searchParams.set('billingCycle', toBillingCycle(cycle));
    return url.href;
  }

  function redirectToAuth(planType, cycle) {
    const returnUrl = planType
      ? getCheckoutIntentUrl(planType, cycle)
      : window.location.href;
    window.location.href = getAuthUrl(returnUrl);
  }

  function redirectToAuthAfterPaint(planType, cycle) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => redirectToAuth(planType, cycle));
    });
  }

  function normalizePlanType(planType) {
    const value = String(planType || 'basic').toLowerCase();
    return PLAN_ORDER[value] === undefined ? 'basic' : value;
  }

  function getCheckoutIntent(params) {
    const plan = String(params.get('plan') || '').toUpperCase();
    const billingCycle = String(params.get('billingCycle') || '').toUpperCase();
    if (plan !== 'PRO' && plan !== 'PREMIUM') return null;
    if (billingCycle !== 'MONTH' && billingCycle !== 'YEAR') return null;
    return {
      planType: plan === 'PREMIUM' ? 'prime' : 'pro',
      cycle: billingCycle === 'YEAR' ? 'yearly' : 'monthly',
    };
  }

  function clearCheckoutIntentParams() {
    const url = new URL(window.location.href);
    url.searchParams.delete('plan');
    url.searchParams.delete('billingCycle');
    window.history.replaceState(
      window.history.state,
      '',
      url.pathname + url.search + url.hash,
    );
  }

  function setPaywallContentBlocked(root, blocked) {
    const content = root.querySelector('.jjb-membership-paywall__inner');
    if (!content) return;
    content.inert = blocked;
    if (blocked) content.setAttribute('aria-hidden', 'true');
    else content.removeAttribute('aria-hidden');
  }

  function setPageScrollLocked(owner, locked) {
    const html = document.documentElement;
    const body = document.body;
    if (!html || !body) return;

    if (locked) {
      if (pageScrollLockOwners.has(owner)) return;
      pageScrollLockOwners.add(owner);
      if (pageScrollLockOwners.size > 1) return;

      const scrollY = window.scrollY || html.scrollTop || body.scrollTop || 0;
      pageScrollLockSnapshot = {
        scrollY,
        htmlOverflow: html.style.overflow,
        htmlOverscrollBehavior: html.style.overscrollBehavior,
        bodyLeft: body.style.left,
        bodyOverflow: body.style.overflow,
        bodyPosition: body.style.position,
        bodyRight: body.style.right,
        bodyTop: body.style.top,
        bodyWidth: body.style.width,
      };
      html.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';
      body.style.left = '0';
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.right = '0';
      body.style.top = '-' + scrollY + 'px';
      body.style.width = '100%';
      return;
    }

    pageScrollLockOwners.delete(owner);
    if (pageScrollLockOwners.size > 0 || !pageScrollLockSnapshot) return;

    const snapshot = pageScrollLockSnapshot;
    pageScrollLockSnapshot = null;
    html.style.overflow = snapshot.htmlOverflow;
    html.style.overscrollBehavior = snapshot.htmlOverscrollBehavior;
    body.style.left = snapshot.bodyLeft;
    body.style.overflow = snapshot.bodyOverflow;
    body.style.position = snapshot.bodyPosition;
    body.style.right = snapshot.bodyRight;
    body.style.top = snapshot.bodyTop;
    body.style.width = snapshot.bodyWidth;
    window.scrollTo(0, snapshot.scrollY);
  }

  function bindComingSoonOverlay(root) {
    const button = root.querySelector(
      '[data-jjb-membership-continue-checkout]',
    );
    if (!button || button.dataset.jjbMembershipContinueBound === 'true') return;
    button.dataset.jjbMembershipContinueBound = 'true';
    button.addEventListener('click', () => {
      window.history.back();
    });
  }

  function showComingSoon(root) {
    const overlay = root.querySelector('[data-jjb-membership-coming-soon]');
    const shell = root.querySelector('[data-jjb-membership-shell]');
    if (!overlay) return;
    bindComingSoonOverlay(root);
    overlay.hidden = false;
    if (shell) shell.hidden = true;
    root.classList.add('is-coming-soon');
    setPageScrollLocked(root, true);
    setPaywallContentBlocked(root, true);
    const button = overlay.querySelector(
      '[data-jjb-membership-continue-checkout]',
    );
    if (button) button.focus({ preventScroll: true });
  }

  function showMembershipShell(root) {
    const overlay = root.querySelector('[data-jjb-membership-coming-soon]');
    const shell = root.querySelector('[data-jjb-membership-shell]');
    if (overlay) overlay.hidden = true;
    if (shell) shell.hidden = false;
    root.classList.remove('is-coming-soon');
    setPageScrollLocked(root, false);
    setPaywallContentBlocked(root, false);
  }

  const UPGRADE_ENTRY_PAGES = new Set([
    'cart',
    'cart_drawer',
    'cart_half',
    'cart_page',
    'cart_whole',
    'checkout',
    'generate',
    'generate_graduation',
  ]);

  function resolveInitialTab(params, defaultTab) {
    const explicitTab = String(params.get('tab') || '').toLowerCase();
    if (explicitTab === 'membership' || explicitTab === 'plans') {
      return explicitTab;
    }
    const entryPage = String(params.get('entry_page') || '').toLowerCase();
    if (UPGRADE_ENTRY_PAGES.has(entryPage) || params.has('plan')) {
      return 'plans';
    }
    return String(defaultTab || '').toLowerCase() === 'membership'
      ? 'membership'
      : 'plans';
  }

  function getTabUrl(href, tab) {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('tab', tab === 'plans' ? 'plans' : 'membership');
    return url.pathname + url.search + url.hash;
  }

  function getMembershipRefreshUrl(href) {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('tab', 'membership');
    url.searchParams.set('refresh_membership', '1');
    return url.href;
  }

  function toFiniteNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function formatCouponValue(value) {
    const number = toFiniteNumber(value);
    if (number === null) return '';
    return Number.isInteger(number) ? String(number) : number.toFixed(2);
  }

  function formatCouponDollars(cents) {
    const amount = toFiniteNumber(cents);
    return amount === null ? '' : '$' + formatCouponValue(amount / 100);
  }

  function normalizeCouponKind(coupon) {
    const kind = String(
      coupon.kind || coupon.couponType || coupon.coupon_type || '',
    ).toUpperCase();
    if (kind === 'FIXED' || kind === 'AMOUNT_OFF' || kind === 'FIXED_AMOUNT') {
      return 'FIXED';
    }
    if (kind === 'PERCENTAGE' || kind === 'PERCENT_OFF') {
      return 'PERCENTAGE';
    }
    return kind;
  }

  function normalizeCoupon(coupon, defaultStatus) {
    const item = coupon || {};
    const kind = normalizeCouponKind(item);
    const amountCents = toFiniteNumber(item.amountCents ?? item.amount_cents);
    const percentage = toFiniteNumber(item.percentage);
    const maxDiscountCents = toFiniteNumber(
      item.maxDiscountCents ?? item.max_discount_cents,
    );
    const expiresAt = item.expiresAt || item.expires_at || '';
    const usedAt = item.usedAt || item.used_at || '';
    const inactiveReason = String(
      item.inactiveReason || item.inactive_reason || '',
    ).toUpperCase();
    let status = String(
      item.status || defaultStatus || 'AVAILABLE',
    ).toUpperCase();
    if (usedAt || inactiveReason === 'USED') status = 'USED';
    else if (inactiveReason === 'EXPIRED') status = 'EXPIRED';
    const isInactive = status !== 'AVAILABLE' && status !== 'ACTIVE';
    const generatedTitle =
      kind === 'FIXED' && amountCents !== null
        ? formatCouponDollars(amountCents)
        : kind === 'PERCENTAGE' && percentage !== null
          ? formatCouponValue(percentage) + '% OFF'
          : '';
    const generatedBadge =
      kind === 'FIXED'
        ? 'No minimum'
        : kind === 'PERCENTAGE' && maxDiscountCents !== null
          ? 'Up to ' + formatCouponDollars(maxDiscountCents)
          : '';
    const title = String(
      generatedTitle ||
        item.title ||
        item.discountLabel ||
        item.code ||
        'Member coupon',
    );
    const badge = String(
      generatedBadge ||
        item.badge ||
        item.minimumLabel ||
        item.minimum_label ||
        item.usageLimitLabel ||
        '',
    );
    const meta =
      status === 'USED'
        ? 'Used'
        : expiresAt
          ? 'Exp: ' + formatMembershipDate(expiresAt)
          : String(item.inactive_reason || 'Member offer');
    return {
      id: item.id || item.code || '',
      kind,
      amountCents,
      percentage,
      maxDiscountCents,
      status,
      expiresAt,
      usedAt,
      title,
      badge,
      meta,
      isInactive,
    };
  }

  function sortAvailableCoupons(coupons) {
    const kindOrder = { FIXED: 0, PERCENTAGE: 1 };
    return coupons.sort((left, right) => {
      const kindDifference =
        (kindOrder[left.kind] ?? 2) - (kindOrder[right.kind] ?? 2);
      if (kindDifference) return kindDifference;
      if (left.kind === 'FIXED') {
        return (right.amountCents ?? -1) - (left.amountCents ?? -1);
      }
      if (left.kind === 'PERCENTAGE') {
        return (right.percentage ?? -1) - (left.percentage ?? -1);
      }
      return 0;
    });
  }

  function normalizeMembershipCoupons(coupons) {
    const available = [];
    const inactive = [];
    const append = (items, defaultStatus) => {
      if (!Array.isArray(items)) return;
      items.forEach((coupon) => {
        const normalized = normalizeCoupon(coupon, defaultStatus);
        (normalized.isInactive ? inactive : available).push(normalized);
      });
    };
    if (Array.isArray(coupons)) {
      append(coupons, 'AVAILABLE');
    } else {
      append(coupons?.available, 'AVAILABLE');
      append(coupons?.inactive, 'EXPIRED');
    }
    return { available: sortAvailableCoupons(available), inactive };
  }

  function getGenerationQuotaProgress(usedValue, limitValue) {
    if (
      usedValue === null ||
      usedValue === undefined ||
      limitValue === null ||
      limitValue === undefined
    ) {
      return null;
    }
    const used = Number(usedValue);
    const limit = Number(limitValue);
    if (!Number.isFinite(used) || !Number.isFinite(limit) || limit <= 0) {
      return null;
    }
    const normalizedUsed = Math.max(0, used);
    const remaining = Math.max(0, limit - normalizedUsed);
    return {
      used: normalizedUsed,
      limit,
      remaining,
      percentage: Math.min(100, (normalizedUsed / limit) * 100),
    };
  }

  function formatBillingCycle(cycle) {
    const value = String(cycle || '').toUpperCase();
    if (value === 'YEAR' || value === 'YEARLY') return 'Yearly';
    if (value === 'MONTH' || value === 'MONTHLY') return 'Monthly';
    return '';
  }

  function formatBillingPlan(plan) {
    const value = String(plan || '').toUpperCase();
    if (value === 'PREMIUM' || value === 'PRIME') return 'Premium';
    if (value === 'PRO') return 'Pro';
    return '';
  }

  function formatBillingMoney(cents, currency) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: String(currency || 'USD').toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value / 100);
    } catch (_) {
      return '$' + (value / 100).toFixed(2);
    }
  }

  function formatBillingDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }

  function normalizeBillingHistory(payload, _membership) {
    const items = Array.isArray(payload) ? payload : payload?.items;
    if (!Array.isArray(items)) return [];
    return items.map((item, index) => {
      const type = String(item?.type || 'INVOICE').toUpperCase();
      // 当前会员状态不能代表历史账单的套餐或周期。
      const plan = formatBillingPlan(item?.plan);
      const cycle = formatBillingCycle(
        item?.billingCycle || item?.billing_cycle,
      );
      let title = 'Billing Record';
      if (type === 'CREDIT_NOTE') title = 'Credit Note';
      else if (type === 'DISPUTE') title = 'Payment Dispute';
      else if (plan) title = plan + ' Plan' + (cycle ? ' - ' + cycle : '');
      const amount = formatBillingMoney(
        item?.totalCents ?? item?.total_cents,
        item?.currency,
      );
      const paymentStatus = String(
        item?.paymentStatus || item?.payment_status || '',
      ).toUpperCase();
      const statusLabel = paymentStatus
        ? paymentStatus.charAt(0) + paymentStatus.slice(1).toLowerCase()
        : '';
      return {
        id: String(item?.id || 'billing-' + index),
        title,
        amount: [amount, statusLabel].filter(Boolean).join(' '),
        createdAt: formatBillingDate(item?.createdAt || item?.created_at),
      };
    });
  }

  function formatLocalDailyResetTime(now = new Date()) {
    const beijingDateParts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(now);
    const getPart = (type) =>
      Number(beijingDateParts.find((part) => part.type === type)?.value);
    const year = getPart('year');
    const month = getPart('month');
    const day = getPart('day');
    const nextBeijingMidnight = new Date(
      Date.UTC(year, month - 1, day + 1) - 8 * 60 * 60 * 1000,
    );

    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(nextBeijingMidnight);
  }

  function formatResetLabel12Hour(label) {
    const value = String(label || '');
    const match = value.match(/\b(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?\b/i);
    if (!match) return value;

    let hour = Number(match[1]);
    const minute = match[2] || '00';
    const meridiem = match[3] ? match[3].toUpperCase() : null;
    if (!Number.isFinite(hour) || hour > 23) return value;
    if (meridiem) hour = (hour % 12) + (meridiem === 'PM' ? 12 : 0);

    const displayHour = hour % 12 || 12;
    const displayMeridiem = hour >= 12 ? 'PM' : 'AM';
    return value.replace(
      match[0],
      displayHour + ':' + minute + ' ' + displayMeridiem,
    );
  }

  window.JJBMembershipPageUtils = {
    canCancelMembershipPlan,
    getButtonState,
    getSelectablePlanTypes,
    getCancelSuccessMessage,
    getGenerationQuotaProgress,
    getMembershipAction,
    getMembershipPlanDescription,
    getMembershipRefreshUrl,
    getTabUrl,
    formatLocalDailyResetTime,
    mapMembershipState,
    normalizeBillingHistory,
    normalizeMembershipCoupons,
    normalizeMembershipState,
    resolveInitialTab,
  };

  function track(eventName, params = {}, options = {}) {
    const payload = Object.assign(
      { page: window.location.pathname, send_to: window.GA_MEASUREMENT_ID },
      params || {},
    );
    try {
      return window?.zlog?.track?.(eventName, payload, {
        statsig: false,
        ...options,
      });
    } catch (_) {}
  }

  function trackMembershipEntryView(root, tab) {
    track('membership_entry_view', {
      entry_page: 'membership',
      current_plan: root.dataset.membershipPlan || 'BASIC',
      tab,
    });
  }

  function getAnalyticsEntryPage(root) {
    const entryPage = String(root.dataset.entryPage || '').toLowerCase();
    if (entryPage === 'generate' || entryPage === 'profile') return entryPage;
    if (entryPage === 'cart_drawer' || entryPage === 'cart_half') {
      return 'cart_half';
    }
    if (
      entryPage === 'cart' ||
      entryPage === 'cart_page' ||
      entryPage === 'cart_whole'
    ) {
      return 'cart_whole';
    }
    if (entryPage === 'checkout') return 'checkout';
    return 'membership';
  }

  async function getMembershipAnalytics(root, includeEntryPage) {
    const entryPage = includeEntryPage
      ? getAnalyticsEntryPage(root)
      : undefined;
    try {
      const analytics =
        await window.jjbCheckoutRuntime?.getAnalytics?.(entryPage);
      if (analytics) return analytics;
    } catch {}
    return entryPage ? { entryPage } : {};
  }

  let popupReadyPromise;
  function waitForMembershipPopup() {
    if (window.jjbMembershipPopup || document.readyState === 'complete')
      return Promise.resolve(window.jjbMembershipPopup);
    // defer可能先于弹窗module执行；等其初始化结束后才判断是否缺少控制器。
    popupReadyPromise ||= new Promise((resolve) => {
      const ready = () => {
        document.removeEventListener('DOMContentLoaded', ready);
        window.removeEventListener('load', ready);
        resolve();
      };
      document.addEventListener('DOMContentLoaded', ready, { once: true });
      window.addEventListener('load', ready, { once: true });
    });
    return popupReadyPromise.then(() => window.jjbMembershipPopup);
  }

  async function createMembershipOperation(root, includeEntryPage) {
    return {
      idempotencyKey: getIdempotencyKey(),
      analytics: await getMembershipAnalytics(root, includeEntryPage),
    };
  }

  function formatMoney(value) {
    const number = Number(value || 0);
    if (number <= 0) return 'Free';
    return '$' + number.toFixed(2);
  }

  function formatCardMoney(value) {
    return formatMoney(value);
  }

  function getPlanDisplayPrice(planType, cycle) {
    const normalized = normalizePlanType(planType);
    const plan = PLANS.find((item) => item.plan_type === normalized);
    if (!plan) return '';
    return formatMoney(
      normalizeBillingCycle(cycle, normalized) === 'yearly'
        ? plan.yearly_first_year_price
        : plan.monthly_price,
    );
  }

  function planName(planType) {
    const normalized = normalizePlanType(planType);
    if (normalized === 'prime') return 'Premium';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  function analyticsPlanName(planType) {
    const normalized = normalizePlanType(planType);
    if (normalized === 'prime') return 'Prime';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  function canUpgradeTo(currentPlan, currentCycle, targetPlan, targetCycle) {
    const current = normalizePlanType(currentPlan);
    const target = normalizePlanType(targetPlan);
    const currentBillingCycle = normalizeBillingCycle(currentCycle, current);
    const targetBillingCycle = normalizeBillingCycle(targetCycle, target);
    if (target === 'basic') return false;
    if (current === 'basic') return true;
    if (current === 'pro' && currentBillingCycle === 'monthly') {
      return (
        (target === 'pro' && targetBillingCycle === 'yearly') ||
        target === 'prime'
      );
    }
    if (current === 'pro' && currentBillingCycle === 'yearly') {
      return target === 'prime' && targetBillingCycle === 'yearly';
    }
    if (current === 'prime' && currentBillingCycle === 'monthly') {
      return target === 'prime' && targetBillingCycle === 'yearly';
    }
    return false;
  }

  function getButtonState(plan, membership, targetCycle) {
    const target = normalizePlanType(plan.plan_type);
    const current = normalizePlanType(membership.plan_type);
    const currentCycle = normalizeBillingCycle(
      membership.billing_cycle,
      current,
    );
    const normalizedTargetCycle = normalizeBillingCycle(targetCycle, target);
    const isCurrentPlan =
      target === current &&
      (current === 'basic' || currentCycle === normalizedTargetCycle);
    const isSubscriptionCancelled =
      current !== 'basic' && membership.vip_subscription_status === 'cancel';

    if (
      target !== 'basic' &&
      (membership.membership_status === 'DUNNING' ||
        (membership.billing_status === 'UNPAID' &&
          ['ACTIVE', 'CANCELLING'].includes(membership.membership_status)))
    ) {
      return {
        label: 'Update payment method',
        disabled: false,
        action: 'payment_method_setup',
      };
    }
    if (hasOpenGiftSignup(membership)) {
      // 待生效签约（赠转付预约）：以预约档位周期为基准渲染——预约本身是
      // Current Plan，更高档位/更长周期可升级换挡（后端取消旧意向建新意向），
      // 其余 Included；非赠送来源（赠送已撤销的残留签约）或旧接口缺少预约
      // 档位时保持整体 pending。
      const pendingPlan = toPlanType(membership.renewal_plan);
      const pendingCycle = normalizeBillingCycle(
        membership.renewal_billing_cycle,
        pendingPlan,
      );
      if (membership.membership_source === 'GIFT' && pendingPlan !== 'basic') {
        if (target === pendingPlan && pendingCycle === normalizedTargetCycle) {
          return { label: 'Current Plan', disabled: true, action: 'current' };
        }
        if (
          canUpgradeTo(pendingPlan, pendingCycle, target, normalizedTargetCycle)
        ) {
          return { label: 'Upgrade', disabled: false, action: 'checkout' };
        }
        return { label: 'Included', disabled: true, action: 'included' };
      }
      return {
        label: 'Subscription pending',
        disabled: true,
        action: 'pending',
      };
    }
    if (membership.membership_source === 'GIFT' && current !== 'basic') {
      if (target !== 'basic' && membership.resume_visible) {
        // 赠转付：向更高档位转付是升级转化显示 Upgrade；同档位/降档转付显示 Resume。
        return {
          label: PLAN_ORDER[target] > PLAN_ORDER[current] ? 'Upgrade' : 'Resume',
          disabled: false,
          action: 'checkout',
        };
      }
      return {
        label: target === current ? 'Current Plan' : 'Included',
        disabled: true,
        action: 'current',
      };
    }
    if (isCurrentPlan && isSubscriptionCancelled) {
      return { label: 'Resume', disabled: false, action: 'resume' };
    }
    if (isCurrentPlan) {
      return { label: 'Current Plan', disabled: true, action: 'current' };
    }
    if (!canUpgradeTo(current, currentCycle, target, normalizedTargetCycle)) {
      return { label: 'Included', disabled: true, action: 'included' };
    }
    return {
      label: target === 'pro' ? 'Get Pro' : 'Get Premium',
      disabled: false,
      action: 'checkout',
    };
  }

  function getMembershipPopupCheckoutPayload(root, button, membership) {
    const planType = normalizePlanType(button.dataset.planType);
    const billingCycle = normalizeBillingCycle(
      root.dataset.billingCycle,
      planType,
    );
    const isUpgrade =
      normalizePlanType(membership && membership.plan_type) !== 'basic';
    return {
      entry_page: isUpgrade ? 'membership' : 'direct',
      plan_type: planType === 'prime' ? 'premium' : planType,
      billing_cycle: billingCycle,
      plan_label: planName(planType) + ' Membership',
      display_price: getPlanDisplayPrice(planType, billingCycle),
      action_type: isUpgrade ? 'upgrade' : 'purchase',
    };
  }

  function getCheckoutSource(root) {
    return root.dataset.returnUrl || window.location.href;
  }

  function createCheckoutSourceHash() {
    if (typeof window.crypto?.randomUUID === 'function') {
      return 'h_' + window.crypto.randomUUID();
    }
    return 'h_' + Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  function storeCheckoutSource(source) {
    const hash = createCheckoutSourceHash();
    try {
      window.localStorage.setItem(
        CHECKOUT_SOURCE_STORAGE_KEY_PREFIX + hash,
        source,
      );
    } catch (_) {}
    return hash;
  }

  function getCheckoutSuccessUrl(root, planType) {
    const successUrl = new URL(
      '/pages/checkout-succeed',
      'https://exn11x-id.myshopify.com',
    );
    successUrl.searchParams.set(
      'source',
      storeCheckoutSource(getCheckoutSource(root)),
    );
    successUrl.searchParams.set(
      'plan',
      planType === 'prime' ? 'PREMIUM' : 'PRO',
    );
    return successUrl.href;
  }

  async function createMembershipCheckout(root, planType, cycle, operation) {
    return requestMembershipApi('/checkouts', {
      method: 'POST',
      body: {
        idempotencyKey: operation.idempotencyKey,
        plan: planType === 'prime' ? 'PREMIUM' : 'PRO',
        billingCycle: toBillingCycle(cycle),
        successUrl: operation.successUrl,
        uiMode: operation.uiMode,
        analytics: operation.analytics,
      },
    });
  }

  async function createMembershipUpgrade(root, planType, cycle, operation) {
    return requestMembershipApi('/upgrades', {
      method: 'POST',
      body: {
        idempotencyKey: operation.idempotencyKey,
        plan: planType === 'prime' ? 'PREMIUM' : 'PRO',
        billingCycle: toBillingCycle(cycle),
        successUrl: operation.successUrl,
        uiMode: operation.uiMode,
        analytics: operation.analytics,
      },
    });
  }

  async function resumeSubscription(operation) {
    return requestMembershipApi('/subscription/resume', {
      method: 'POST',
      body: Object.assign(
        { idempotencyKey: operation.idempotencyKey },
        Object.keys(operation.analytics).length
          ? { analytics: operation.analytics }
          : {},
      ),
    });
  }

  async function cancelSubscription(idempotencyKey) {
    return requestMembershipApi('/subscription/cancel', {
      method: 'POST',
      body: { idempotencyKey },
    });
  }

  async function redirectToCheckout(checkout, { action, planType, cycle }) {
    const url = String(checkout?.url || '');
    if (!/^https?:\/\//i.test(url)) {
      throw new Error('Checkout is not ready. Please try again.');
    }
    await track('membership_checkout_redirect', {
      customerId: window.theme?.customerId || null,
      checkoutId: checkout?.id || checkout?.checkoutId || null,
      action,
      plan:
        planType === 'prime' ? 'PREMIUM' : String(planType || '').toUpperCase(),
      billingCycle: toBillingCycle(cycle),
    });
    window.zlog?.flushTracking?.();
    window.location.href = url;
  }

  async function beginCheckout(root, planType, cycle, membership) {
    const current = normalizePlanType(membership && membership.plan_type);
    const giftResume =
      membership?.membership_source === 'GIFT' &&
      (membership?.resume_visible || hasOpenGiftSignup(membership));
    // 共用同一控制器，登录回跳、套餐页和弹窗不会各自再创建一次会话。
    const popup = await waitForMembershipPopup();
    if (popup?.openCheckout) {
      // 等待本轮 checkout 启动完成（hosted 已触发跳转 / 半屏已就绪或出错），
      // 让 GET PRO 等入口按钮的 loading 保持到流程结束而不是闪一下即被重置。
      await popup.openCheckout({
        entry_page: getAnalyticsEntryPage(root),
        plan_type: planType === 'prime' ? 'premium' : 'pro',
        billing_cycle: cycle,
        plan_label:
          planType === 'prime' ? 'Premium Membership' : 'Pro Membership',
        display_price: getPlanDisplayPrice(planType, cycle),
        action_type: requiresPaymentMethodUpdate({
          status: membership?.membership_status,
          billingStatus: membership?.billing_status,
        })
          ? 'payment_method_setup'
          : current === 'basic'
            ? 'purchase'
            : 'upgrade',
        giftId: membership?.gift_id,
        purpose: giftResume
          ? 'GIFT_RESUME'
          : requiresPaymentMethodUpdate({
                status: membership?.membership_status,
                billingStatus: membership?.billing_status,
              })
            ? 'SETUP'
            : 'PURCHASE',
      });
      return;
    }
    const latestMembership = await requestMembershipApi('/me');
    updateMembershipOverview(root, latestMembership);
    if (requiresPaymentMethodUpdate(latestMembership)) {
      throw new Error(
        'Please open the membership page to update your payment method.',
      );
    }
    if (
      normalizeMembershipState(latestMembership).membershipSource === 'GIFT' ||
      hasOpenGiftSignup(latestMembership)
    ) {
      throw new Error(
        'Please reopen the membership page to resume your gift membership.',
      );
    }
    // 未渲染弹窗section的模板仍可以通过Hosted完成购买。
    const runtime = window.jjbCheckoutRuntime;
    const attempt = runtime?.createAttempt?.('HOSTED', 'missing_popup');
    const measure = (phase, operation) =>
      attempt ? attempt.measure(phase, operation) : operation();
    try {
      const analytics = await measure('analytics', () =>
        getMembershipAnalytics(root, true),
      );
      const intent = runtime?.getIntent?.({
        plan: planType === 'prime' ? 'PREMIUM' : 'PRO',
        billingCycle: toBillingCycle(cycle),
        uiMode: 'HOSTED',
        environment: isTestMode() ? 'test' : 'prod',
        analytics,
        createSuccessUrl: () => getCheckoutSuccessUrl(root, planType),
      }) || {
        idempotencyKey: getIdempotencyKey(),
        successUrl: getCheckoutSuccessUrl(root, planType),
        analytics,
      };
      const checkout = await measure('backend', () =>
        createMembershipCheckout(root, planType, cycle, {
          ...intent,
          uiMode: 'HOSTED',
        }),
      );
      if (
        [
          'COMPLETED',
          'APPLIED',
          'CANCELLED',
          'EXPIRED',
          'FAILED',
          'REFUNDED',
        ].includes(checkout?.status)
      ) {
        runtime?.retireIntent?.(intent.idempotencyKey);
        throw new Error('Checkout has ended. Please try again.');
      }
      await attempt?.finish('redirect');
      await redirectToCheckout(checkout, {
        action: current === 'basic' ? 'buy' : 'upgrade',
        planType,
        cycle,
      });
    } catch (error) {
      attempt?.finish('error');
      throw error;
    }
  }

  function getDefaultSelectedPlanType(membership) {
    const current = normalizePlanType(membership && membership.plan_type);
    if (current === 'basic') return 'pro';
    if (current === 'pro') return 'prime';
    return 'prime';
  }

  function getSelectablePlanTypes(membership, availablePlanTypes, targetCycle) {
    const available =
      Array.isArray(availablePlanTypes) && availablePlanTypes.length
        ? availablePlanTypes
        : ['basic', 'pro', 'prime'];
    if (hasOpenGiftSignup(membership)) {
      const pendingPlan = toPlanType(membership.renewal_plan);
      const pendingCycle = normalizeBillingCycle(
        membership.renewal_billing_cycle,
        pendingPlan,
      );
      if (membership.membership_source === 'GIFT' && pendingPlan !== 'basic') {
        return available.filter((planType) =>
          canUpgradeTo(pendingPlan, pendingCycle, planType, targetCycle),
        );
      }
      return [];
    }
    if (
      membership?.membership_source === 'GIFT' &&
      membership.plan_type !== 'basic'
    ) {
      return membership.resume_visible
        ? available.filter((p) => p !== 'basic')
        : [];
    }
    return available.filter((planType) =>
      canUpgradeTo(
        membership && membership.plan_type,
        membership && membership.billing_cycle,
        planType,
        targetCycle,
      ),
    );
  }

  function resolveSelectedPlanType(
    root,
    membership,
    availablePlanTypes,
    targetCycle,
  ) {
    let selected = normalizePlanType(root && root.dataset.selectedPlan);
    const selectable = getSelectablePlanTypes(
      membership,
      availablePlanTypes,
      targetCycle,
    );
    if (selectable.indexOf(selected) !== -1) return selected;
    selected = getDefaultSelectedPlanType(membership);
    if (selectable.indexOf(selected) !== -1) return selected;
    return selectable[0] || '';
  }

  function syncSelectedPlan(root, planType) {
    const selected = normalizePlanType(planType);
    const card = root.querySelector('[data-plan-card="' + selected + '"]');
    if (!card || card.dataset.selectable !== 'true') return;
    root.dataset.selectedPlan = selected;
    root.querySelectorAll('[data-plan-card]').forEach((planCard) => {
      const isSelected = planCard.dataset.planCard === selected;
      planCard.classList.toggle('is-selected', isSelected);
      planCard.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  }

  function getPlanContent(planType) {
    const common = [
      'Up to 3 generations at a time',
      'Free digital crafting',
      'Faster generation speed',
      'Higher generation quality',
      'Priority support',
    ];
    if (planType === 'basic') {
      return {
        included: [
          '<span class="jjb-membership-card__limited-time">Limited time</span><mark>10% OFF</mark> or more on all products',
          FEATURE_DIVIDER,
          '<span class="jjb-membership-card__limited-time">Limited time</span><mark>40</mark> free generations / day',
          '<span class="jjb-membership-card__limited-time">Limited time</span>Up to 3 generations at a time',
          '<span class="jjb-membership-card__limited-time">Limited time</span>Free digital crafting',
          'General generation speed',
        ],
        comingSoon: [],
      };
    }
    if (planType === 'pro') {
      return {
        included: [
          '<span class="jjb-membership-card__limited-time">Limited time</span><mark>$20 OFF</mark> one order / month',
          '<mark>2 &times; 20% OFF</mark> coupons / month',
          '<mark>10% OFF</mark> or more on all products',
          FEATURE_DIVIDER,
          '<mark>100</mark> generations / day',
        ].concat(common),
        comingSoon: ['Free shipping coupons', 'Exclusive templates'],
      };
    }
    return {
      included: [
        '<span class="jjb-membership-card__limited-time">Limited time</span><mark>$50 OFF</mark> one order / month',
        '<mark>4 &times; 20% OFF</mark> coupons / month',
        '<mark>10% OFF</mark> or more on all products',
        FEATURE_DIVIDER,
        '<mark>200</mark> generations / day',
      ].concat(common),
      comingSoon: [
        'Birthday gift',
        'Free shipping coupons',
        'Exclusive templates',
      ],
    };
  }

  function renderFeatureList(items, disabled) {
    if (!items || !items.length) return '';
    return (
      '<ul class="jjb-membership-card__features' +
      (disabled ? ' is-disabled' : '') +
      '">' +
      items
        .map((item) => {
          if (item === FEATURE_DIVIDER) {
            return '<li class="jjb-membership-card__feature-divider" aria-hidden="true"></li>';
          }
          return (
            '<li>' +
            renderFeatureIcon(item, disabled) +
            '<span>' +
            item +
            '</span></li>'
          );
        })
        .join('') +
      '</ul>'
    );
  }

  function renderFeatureIcon(item, disabled) {
    if (!disabled) {
      return '<span class="jjb-membership-card__check" aria-hidden="true"></span>';
    }
    return '<svg class="jjb-membership-card__soon-icon" width="6" height="6" viewBox="0 0 6 6" fill="none" aria-hidden="true" focusable="false"><circle cx="3" cy="3" r="3" fill="#D9D9D9"/></svg>';
  }

  function renderPlanCard(
    plan,
    membership,
    cycle,
    selectedPlanType,
    selectablePlanTypes,
    sectionId,
  ) {
    const planType = normalizePlanType(plan.plan_type);
    const button = getButtonState(plan, membership, cycle);
    const isSelected = planType === selectedPlanType;
    const isSelectable =
      Array.isArray(selectablePlanTypes) &&
      selectablePlanTypes.indexOf(planType) !== -1;
    const priceHtml =
      cycle === 'yearly' && planType !== 'basic'
        ? '<div class="jjb-membership-card__price-stack"><p class="jjb-membership-card__price"><span>' +
          formatCardMoney(plan.yearly_first_year_price) +
          '</span><span class="jjb-membership-card__original-price">' +
          formatMoney(plan.yearly_original_price) +
          '</span><span style="font-size:18px;font-family: \'Outfit\';margin-right: 0;">/</span><small>year</small></p><p class="jjb-membership-card__note">20% OFF your first annual plan</p></div>'
        : cycle === 'yearly' && planType === 'basic'
          ? '<div class="jjb-membership-card__price-stack jjb-membership-card__price-stack--basic-yearly"><p class="jjb-membership-card__price"><span>' +
            formatCardMoney(plan.monthly_price) +
            '</span></p><p class="jjb-membership-card__note jjb-membership-card__note--placeholder" aria-hidden="true"></p></div>'
          : '<p class="jjb-membership-card__price"><span>' +
            formatCardMoney(plan.monthly_price) +
            '</span>' +
            (planType === 'basic' ? '' : '<small>/ month</small>') +
            '</p>';
    const planContent = getPlanContent(planType);
    const headingId =
      'plan-' + (planType === 'prime' ? 'premium' : planType) + '-' + sectionId;

    return (
      '<article class="jjb-membership-card' +
      ' is-plan-' +
      planType +
      (isSelected ? ' is-selected' : '') +
      (isSelectable ? ' is-selectable' : ' is-locked') +
      '" data-plan-card="' +
      planType +
      '" data-selectable="' +
      (isSelectable ? 'true' : 'false') +
      '" tabindex="' +
      (isSelectable ? '0' : '-1') +
      '" aria-labelledby="' +
      headingId +
      '" aria-selected="' +
      (isSelected ? 'true' : 'false') +
      '">' +
      '<div class="jjb-membership-card__header"><h3 id="' +
      headingId +
      '" class="jjb-membership-card__title">' +
      planName(planType) +
      '</h3>' +
      (planType === 'pro'
        ? '<span class="jjb-membership-card__value-badge">Most Popular</span>'
        : '') +
      priceHtml +
      '</div>' +
      '<button type="button" class="jjb-membership-card__button" data-plan-type="' +
      planType +
      '" data-action="' +
      button.action +
      '"' +
      (button.disabled ? ' disabled' : '') +
      '>' +
      button.label +
      '</button>' +
      '<div class="jjb-membership-card__section"><p>INcludes</p>' +
      renderFeatureList(planContent.included, false) +
      '</div>' +
      (planContent.comingSoon.length
        ? '<div class="jjb-membership-card__section jjb-membership-card__section--soon"><p>Coming Soon</p>' +
          renderFeatureList(planContent.comingSoon, true) +
          '</div>'
        : '') +
      '</article>'
    );
  }

  function renderPaywall(root) {
    if (!root) return;
    const state = paywallStates.get(root) || {
      membership: getCurrentMembership(),
      plans: PLANS,
    };
    paywallStates.set(root, state);
    const cycle = root.dataset.billingCycle || 'monthly';
    const cards = root.querySelector('[data-jjb-membership-cards]');
    const membership = state.membership;
    const plans = state.plans;
    root.classList.toggle('is-yearly', cycle === 'yearly');

    if (cards) {
      const visiblePlans = plans;
      const availablePlanTypes = visiblePlans.map((plan) =>
        normalizePlanType(plan.plan_type),
      );
      const selectedPlanType = resolveSelectedPlanType(
        root,
        membership,
        availablePlanTypes,
        cycle,
      );
      const selectablePlanTypes = getSelectablePlanTypes(
        membership,
        availablePlanTypes,
        cycle,
      );
      root.dataset.selectedPlan = selectedPlanType;
      cards.innerHTML = visiblePlans
        .map((plan) =>
          renderPlanCard(
            plan,
            membership,
            cycle,
            selectedPlanType,
            selectablePlanTypes,
            root.id,
          ),
        )
        .join('');
    }

    root.querySelectorAll('[data-billing-cycle]').forEach((control) => {
      const isActive = control.dataset.billingCycle === cycle;
      control.classList.toggle('is-active', isActive);
      control.setAttribute('aria-selected', isActive ? 'true' : 'false');
      control.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    root.querySelectorAll('[data-plan-card]').forEach((card) => {
      card.addEventListener('click', () => {
        syncSelectedPlan(root, card.dataset.planCard);
      });
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (event.target !== card) return;
        event.preventDefault();
        syncSelectedPlan(root, card.dataset.planCard);
      });
    });
    root.querySelectorAll('[data-plan-type]').forEach((button) => {
      const prepare = (event) => {
        if (
          button.dataset.action !== 'checkout' ||
          !isCustomerLoggedIn() ||
          paywallStates.get(root)?.membership?.plan_type !== 'basic'
        )
          return;
        const requestedCycle = root.dataset.billingCycle || 'monthly';
        void waitForMembershipPopup()
          .then((popup) => {
            if (
              !button.isConnected ||
              (root.dataset.billingCycle || 'monthly') !== requestedCycle
            )
              return;
            return popup?.prepareCheckout?.({
              entry_page: getAnalyticsEntryPage(root),
              plan_type:
                button.dataset.planType === 'prime' ? 'premium' : 'pro',
              billing_cycle: requestedCycle,
              plan_label:
                button.dataset.planType === 'prime'
                  ? 'Premium Membership'
                  : 'Pro Membership',
              display_price: getPlanDisplayPrice(
                button.dataset.planType,
                requestedCycle,
              ),
              action_type: 'purchase',
              trustTarget: button,
              freezeTrust: event?.type === 'pointerdown',
            });
          })
          .catch(() => {});
      };
      button.addEventListener('pointerenter', prepare);
      button.addEventListener('focus', prepare);
      button.addEventListener('pointerdown', prepare);
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        syncSelectedPlan(root, button.dataset.planType);
        const action = button.dataset.action;
        if (action === 'checkout' || action === 'resume') {
          track('membership_plan_click', {
            plan_type: analyticsPlanName(button.dataset.planType),
            billing_cycle: cycle,
            action_type:
              action === 'resume' || membership.membership_source === 'GIFT'
                ? 'resume'
                : membership.plan_type === 'basic'
                  ? 'purchase'
                  : 'upgrade',
            entry_page: root.dataset.entryPage || 'direct',
          });
        }
        if (action === 'payment_method_setup') {
          handleCheckout(root, button.dataset.planType, button);
        }
        if (action === 'resume') {
          handleResumeSubscription(root, button);
        }
        if (action === 'checkout')
          handleCheckout(root, button.dataset.planType, button);
      });
    });
    if (!state.impressionTracked) {
      state.impressionTracked = true;
      track('membership_plan_impression', {entry_page: root.dataset.entryPage || 'direct'}, { statsig: true });
    }
  }

  function setPlanButtonLoading(root, button) {
    root.classList.add('is-loading');
    root.querySelectorAll('[data-plan-type]').forEach((control) => {
      control.disabled = true;
    });
    if (!button) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-label', 'Loading');
    button.innerHTML =
      '<span class="jjb-membership-card__button-spinner" aria-hidden="true"></span>';
  }

  async function handleCheckout(root, planType, triggerButton) {
    const state = paywallStates.get(root);
    if (!state || state.loading) return;
    setPlanButtonLoading(root, triggerButton);
    if (!isCustomerLoggedIn()) {
      redirectToAuthAfterPaint(
        normalizePlanType(planType),
        root.dataset.billingCycle || 'monthly',
      );
      return;
    }
    state.loading = true;
    try {
      await beginCheckout(
        root,
        normalizePlanType(planType),
        root.dataset.billingCycle || 'monthly',
        state.membership,
      );
    } catch (error) {
      showMembershipRequestError(error);
    } finally {
      state.loading = false;
      root.classList.remove('is-loading');
      renderPaywall(root);
    }
  }

  async function handleResumeSubscription(root, triggerButton) {
    const state = paywallStates.get(root);
    if (!state || state.loading) return;
    setPlanButtonLoading(root, triggerButton);
    if (!isCustomerLoggedIn()) {
      redirectToAuthAfterPaint();
      return;
    }
    state.loading = true;
    try {
      const operation = await createMembershipOperation(root, false);
      const summary = await resumeSubscription(operation);
      const refreshed = await refreshMembershipAfterMutation(root);
      if (!refreshed) {
        if (summary) updateMembershipOverview(root, summary);
        state.membership = summary
          ? mapMembershipSummary(summary)
          : getCurrentMembership();
      }
      if (typeof window.toast === 'function') {
        window.toast('Auto-renewal resumed');
      }
    } catch (error) {
      if (typeof window.toast === 'function') {
        window.toast(getErrorMessage(error));
      }
    } finally {
      state.loading = false;
      root.classList.remove('is-loading');
      renderPaywall(root);
    }
  }

  async function handleCancelSubscription(root, button, dialog) {
    const state = paywallStates.get(root);
    if (state?.loading || root.dataset.membershipCancelLoading === 'true')
      return;
    if (!isCustomerLoggedIn()) {
      redirectToAuth();
      return;
    }
    const membership = state?.membership || getCurrentMembership();
    const planType = analyticsPlanName(membership.plan_type);
    track('membership_cancel_subscription_click', {
      plan_type: planType,
      membership_level: planType,
      billing_cycle: membership.billing_cycle || 'monthly',
    });
    const originalLabel = button.textContent;
    if (state) state.loading = true;
    root.dataset.membershipCancelLoading = 'true';
    root.classList.add('is-loading');
    button.disabled = true;
    button.textContent = 'Cancelling...';
    root.querySelectorAll('[data-plan-type]').forEach((control) => {
      control.disabled = true;
    });
    try {
      const membershipBeforeCancellation = getThemeMembershipState();
      const summary = await cancelSubscription(getIdempotencyKey());
      const refreshed = await refreshMembershipAfterMutation(root);
      if (!refreshed && summary) {
        updateMembershipOverview(root, summary);
        if (state) state.membership = mapMembershipSummary(summary);
      }
      dialog?.close();
      if (typeof window.toast === 'function') {
        window.toast(
          getCancelSuccessMessage(
            Object.assign({}, membershipBeforeCancellation, summary || {}),
          ),
          undefined,
          { wrap: true },
        );
      }
    } catch (error) {
      if (typeof window.toast === 'function') {
        window.toast(getErrorMessage(error));
      }
    } finally {
      if (state) state.loading = false;
      delete root.dataset.membershipCancelLoading;
      root.classList.remove('is-loading');
      button.disabled = false;
      button.textContent = originalLabel;
      if (state) renderPaywall(root);
    }
  }

  function bindCancelSubscriptionTest(root) {
    const button = root.querySelector('[data-jjb-membership-cancel-test]');
    if (!button) return;
    button.addEventListener('click', () => {
      handleCancelSubscription(root, button);
    });
  }

  function bindPaywallAccordion(root) {
    const qa = root.querySelector('[data-jjb-membership-qa]');
    if (!qa || qa.dataset.jjbMembershipAccordionBound === 'true') return;
    qa.dataset.jjbMembershipAccordionBound = 'true';
    qa.querySelectorAll('details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        qa.querySelectorAll('details[open]').forEach((item) => {
          if (item !== details) item.open = false;
        });
      });
    });
  }

  function continueCheckoutIntent(root, checkoutIntent) {
    if (autoCheckoutHandled || !isCustomerLoggedIn() || !checkoutIntent) {
      return;
    }
    autoCheckoutHandled = true;
    root.dataset.billingCycle = checkoutIntent.cycle;
    root.dataset.selectedPlan = checkoutIntent.planType;
    const state = paywallStates.get(root);
    const plan = PLANS.find(
      (item) => item.plan_type === checkoutIntent.planType,
    );
    const buttonState = getButtonState(
      plan || { plan_type: checkoutIntent.planType },
      state.membership,
      checkoutIntent.cycle,
    );
    clearCheckoutIntentParams();
    renderPaywall(root);
    if (buttonState.action === 'checkout') {
      window.setTimeout(() => {
        const button = root.querySelector(
          '[data-plan-type="' + checkoutIntent.planType + '"]',
        );
        handleCheckout(root, checkoutIntent.planType, button);
      }, 0);
    } else if (typeof window.toast === 'function') {
      window.toast('The selected membership plan is no longer available.');
    }
  }

  function formatMembershipDate(value) {
    if (!value) return 'No expiry';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  function formatMembershipPlan(plan) {
    const value = String(plan || 'BASIC').toLowerCase();
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  function updateMembershipPlanIcons(root, plan) {
    const normalizedPlan = String(plan || 'BASIC').toUpperCase();
    const iconKey =
      normalizedPlan === 'PREMIUM' || normalizedPlan === 'PRIME'
        ? 'premiumIcon'
        : normalizedPlan === 'PRO'
          ? 'proIcon'
          : 'basicIcon';
    const iconHeight = iconKey === 'basicIcon' ? 18 : 16;
    root.querySelectorAll('[data-membership-plan-icon]').forEach((icon) => {
      const source = icon.dataset[iconKey];
      if (!source) return;
      icon.src = source;
      icon.height = iconHeight;
      icon.setAttribute('height', String(iconHeight));
    });
  }

  function getCancelSuccessMessage(membership) {
    const state = normalizeMembershipState(membership);
    const plan = state.plan === 'PRIME' ? 'PREMIUM' : state.plan;
    if (
      (plan !== 'PRO' && plan !== 'PREMIUM') ||
      !isFutureTime(state.paidThrough)
    ) {
      return 'Auto-renewal canceled.';
    }
    return (
      'Auto-renewal canceled. Your ' +
      formatMembershipPlan(plan) +
      ' plan stays active until ' +
      formatMembershipDate(state.paidThrough) +
      '.'
    );
  }

  function getMembershipPlanDescription(state) {
    state = normalizeMembershipState(state);
    if (requiresPaymentMethodUpdate(state)) {
      return state.plan === 'BASIC'
        ? 'Your renewal payment is overdue. Update your payment method. Paid benefits resume after payment is confirmed.'
        : 'Your renewal payment is overdue. Update your payment method to continue your subscription.';
    }
    if (hasOpenGiftSignup(state)) {
      return ['SUBSCRIPTION_SCHEDULED', 'SCHEDULED'].includes(
        state.renewalStatus,
      )
        ? 'Your subscription starts on ' +
            formatMembershipDate(state.renewalScheduledAt) +
            '.'
        : 'Your subscription setup is pending. Complete setup or cancel it before choosing another plan.';
    }
    if (state.membershipSource === 'GIFT') {
      if (mapMembershipState(state).plan_type === 'basic')
        return 'Your gift membership has ended.';
      // 有限期赠送沿用赠送功能上线前的到期文案；永久赠送按等级展示。
      return state.giftEndsAt
        ? 'Your plan expires on ' + formatMembershipDate(state.giftEndsAt)
        : 'Your ' +
            formatMembershipPlan(state.plan) +
            ' membership has no expiration date.';
    }
    if (state.plan === 'BASIC') return 'Members save $20+';
    if (state.cancelAtPeriodEnd && state.paidThrough) {
      return 'Your plan expires on ' + formatMembershipDate(state.paidThrough);
    }
    if (state.cancelAtPeriodEnd) {
      return 'Your plan will not renew.';
    }
    if (state.paidThrough) {
      return (
        'Your plan is active and renews on ' +
        formatMembershipDate(state.paidThrough) +
        '.'
      );
    }
    return 'Your membership is active.';
  }

  function getMembershipAction(membership) {
    const state = normalizeMembershipState(membership);
    const plan = state.plan === 'PRIME' ? 'PREMIUM' : state.plan;
    const summary = mapMembershipState(state);
    if (requiresPaymentMethodUpdate(state)) return 'Update payment method';
    if (state.renewalStatus === 'SETUP_PENDING' && state.renewalSignupId)
      return 'Complete setup';
    if (hasOpenGiftSignup(state)) return 'View';
    if (state.membershipSource === 'GIFT' && summary.plan_type !== 'basic')
      return summary.resume_visible ? 'Resume' : 'View';
    return summary.vip_subscription_status === 'resume' ||
      summary.vip_subscription_status === 'cancel'
      ? 'Resume'
      : plan === 'PREMIUM' && summary.plan_type !== 'basic'
        ? 'View'
        : 'Upgrade';
  }

  function publishMembershipStateUpdate(state) {
    window.dispatchEvent(
      new CustomEvent('jjb:membership-state-updated', {
        detail: {
          plan: state.plan,
          status: state.status,
          paidThrough: state.paidThrough,
          billingCycle: state.billingCycle,
          cancelAtPeriodEnd: state.cancelAtPeriodEnd,
          isLoggedIn: isCustomerLoggedIn(),
        },
      }),
    );
  }

  function updateMembershipOverview(root, membership, options) {
    if (!membership) return;
    const state = normalizeMembershipState(membership);
    const plan = state.plan === 'PRIME' ? 'PREMIUM' : state.plan;
    const summary = mapMembershipState(state);
    const action = getMembershipAction(state);
    root.dataset.membershipPlan = plan;
    root.dataset.membershipStatus = state.status;
    root.dataset.membershipPaymentUpdate = String(
      requiresPaymentMethodUpdate(state),
    );
    window.JJB_CUSTOMER_MEMBERSHIP_STATE = state;
    window.JJB_CUSTOMER_MEMBERSHIP_PLAN = plan;
    window.JJB_CUSTOMER_MEMBERSHIP_STATUS = state.status;
    window.JJB_CUSTOMER_MEMBERSHIP_PAID_THROUGH = state.paidThrough;
    window.JJB_CUSTOMER_MEMBERSHIP_BILLING_CYCLE = state.billingCycle;
    window.JJB_CUSTOMER_MEMBERSHIP_SOURCE = state.membershipSource;
    window.JJB_CUSTOMER_GIFT_ID = state.giftId;
    window.JJB_CUSTOMER_GIFT_ENDS_AT = state.giftEndsAt;
    window.JJB_CUSTOMER_RESUME_VISIBLE = state.resumeVisible;
    window.JJB_CUSTOMER_MEMBERSHIP_AUTO_RENEW = state.autoRenew;
    window.JJB_CUSTOMER_MEMBERSHIP_CANCEL_AT_PERIOD_END =
      state.cancelAtPeriodEnd;
    if (window.theme) {
      window.theme.membershipState = state;
      window.theme.membershipPlan = plan;
      window.theme.membershipStatus = state.status;
      window.theme.membershipPaidThrough = state.paidThrough;
      window.theme.membershipBillingCycle = state.billingCycle;
      window.theme.membershipSource = state.membershipSource;
      window.theme.giftId = state.giftId;
      window.theme.giftEndsAt = state.giftEndsAt;
      window.theme.resumeVisible = state.resumeVisible;
      window.theme.membershipAutoRenew = state.autoRenew;
      window.theme.membershipCancelAtPeriodEnd = state.cancelAtPeriodEnd;
    }
    if (options && options.publishMembershipState) {
      publishMembershipStateUpdate(state);
    }
    root.querySelectorAll('[data-membership-plan]').forEach((node) => {
      node.textContent = formatMembershipPlan(plan);
    });
    updateMembershipPlanIcons(root, plan);
    root
      .querySelectorAll('[data-membership-plan-description]')
      .forEach((node) => {
        node.textContent = getMembershipPlanDescription(state);
      });
    root.querySelectorAll('[data-membership-status]').forEach((node) => {
      node.textContent = state.status === 'BASIC' ? 'Free' : state.status;
    });
    root.querySelectorAll('[data-membership-paid-through]').forEach((node) => {
      node.textContent = formatMembershipDate(state.paidThrough);
    });
    root.querySelectorAll('[data-membership-billing-cycle]').forEach((node) => {
      node.textContent = state.billingCycle || 'Free';
    });
    root.querySelectorAll('[data-membership-open-plans]').forEach((node) => {
      node.textContent = action;
    });
    setMembershipCancelVisibility(root, state);
    root
      .querySelectorAll('[data-membership-cancel-plan-name]')
      .forEach((node) => {
        node.textContent = formatMembershipPlan(plan);
      });
    root
      .querySelectorAll('[data-membership-cancel-paid-through]')
      .forEach((node) => {
        node.textContent = formatMembershipDate(state.paidThrough);
      });
  }

  function setMembershipCancelVisibility(root, membership) {
    const visible = canCancelMembershipPlan(membership);
    root.querySelectorAll('[data-membership-cancel-plan]').forEach((node) => {
      node.hidden = !visible;
      node.classList.toggle('is-hidden', !visible);
      node.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
  }

  function renderCoupons(root, coupons, errorMessage) {
    const availableList = root.querySelector(
      '[data-membership-available-coupon-list]',
    );
    const expiredList = root.querySelector(
      '[data-membership-expired-coupon-list]',
    );
    if (!availableList || !expiredList) return;
    const { available, inactive } = normalizeMembershipCoupons(coupons);
    renderCouponList(
      availableList,
      available,
      errorMessage || 'No membership coupons available yet.',
    );
    renderCouponList(
      expiredList,
      inactive,
      errorMessage || 'No expired coupons yet.',
    );
  }

  function renderCouponList(list, items, emptyMessage) {
    list.replaceChildren();
    if (!items.length) {
      if (!emptyMessage) return;
      const empty = document.createElement('li');
      empty.className = 'is-empty';
      empty.textContent = emptyMessage;
      list.appendChild(empty);
      return;
    }
    items.forEach((coupon) => {
      const item = document.createElement('li');
      const title = document.createElement('strong');
      const meta = document.createElement('span');
      const badge = document.createElement('small');
      item.className = 'jjb-membership-overview__coupon-card';
      if (coupon.kind) item.classList.add('is-' + coupon.kind.toLowerCase());
      if (coupon.isInactive) item.classList.add('is-inactive');
      item.dataset.couponStatus = coupon.status;
      title.textContent = coupon.title;
      meta.textContent = coupon.meta;
      badge.textContent = coupon.badge;
      item.append(title, meta);
      if (badge.textContent) item.appendChild(badge);
      list.appendChild(item);
    });
  }

  function renderGenerationQuota(root, quota) {
    const usedNode = root.querySelector('[data-membership-generation-used]');
    const limitNode = root.querySelector('[data-membership-generation-limit]');
    const progress = root.querySelector(
      '[data-membership-generation-progress]',
    );
    const resetNode = root.querySelector('[data-membership-generation-reset]');
    if (!usedNode || !limitNode || !progress || !resetNode) return;
    const quotaProgress = getGenerationQuotaProgress(quota?.used, quota?.limit);
    const progressBar = progress.querySelector('span');
    if (!quotaProgress) {
      usedNode.textContent = '--';
      limitNode.textContent = '--';
      progress.classList.remove('is-exhausted');
      progress.classList.remove('is-empty');
      progress.setAttribute('aria-valuemax', '0');
      progress.setAttribute('aria-valuenow', '0');
      progress.setAttribute(
        'aria-valuetext',
        'Generation usage is unavailable.',
      );
      if (progressBar) progressBar.style.width = '0%';
      resetNode.textContent = 'Generation usage is unavailable.';
      return;
    }
    usedNode.textContent = quotaProgress.used.toLocaleString('en');
    limitNode.textContent = quotaProgress.limit.toLocaleString('en');
    progress.classList.toggle('is-exhausted', quotaProgress.remaining === 0);
    progress.classList.toggle('is-empty', quotaProgress.used === 0);
    progress.setAttribute('aria-valuemax', String(quotaProgress.limit));
    progress.setAttribute('aria-valuenow', String(quotaProgress.used));
    progress.setAttribute(
      'aria-valuetext',
      'Used: ' + quotaProgress.used + ' / ' + quotaProgress.limit,
    );
    if (progressBar) {
      progressBar.style.width = quotaProgress.percentage + '%';
    }
    const resetLabel =
      quota.resetLabel ||
      quota.reset_label ||
      'Resets daily at ' + formatLocalDailyResetTime();
    resetNode.textContent = formatResetLabel12Hour(resetLabel);
  }

  function renderBillingHistory(root, billing) {
    const list = root.querySelector('[data-membership-billing-list]');
    const status = root.querySelector('[data-membership-billing-status]');
    if (!list || !status) return;
    const items = normalizeBillingHistory(billing, getThemeMembershipState());
    list.replaceChildren();
    if (!items.length) {
      status.textContent = 'No billing history yet.';
      status.hidden = false;
      return;
    }
    status.hidden = true;
    items.forEach((billingItem) => {
      const item = document.createElement('li');
      const heading = document.createElement('div');
      const title = document.createElement('strong');
      const amount = document.createElement('b');
      const date = document.createElement('time');
      item.className = 'jjb-membership-overview__billing-card';
      item.dataset.billingId = billingItem.id;
      title.textContent = billingItem.title;
      amount.textContent = billingItem.amount;
      date.textContent = billingItem.createdAt;
      heading.append(title, amount);
      item.append(heading, date);
      list.appendChild(item);
    });
  }

  async function loadMembershipOverviewData(root) {
    if (!isCustomerLoggedIn()) return;
    // 每块响应独立渲染；仍等待整批结束，保留后续身份更新和Tab补读的顺序。
    const updates = await Promise.allSettled([
      requestMembershipApi('/coupons?includeExpired=true').then(
        (coupons) => renderCoupons(root, coupons),
        () => renderCoupons(root, null, 'Membership coupons are unavailable.'),
      ),
      requestMembershipApi('/quota').then(
        (quota) => renderGenerationQuota(root, quota),
        () => renderGenerationQuota(root, null),
      ),
      requestMembershipApi('/billing?pageSize=20').then(
        (billing) => renderBillingHistory(root, billing),
        () => {
          renderBillingHistory(root, { items: [] });
          const status = root.querySelector('[data-membership-billing-status]');
          if (status) {
            status.textContent = 'Billing history is unavailable.';
            status.hidden = false;
          }
        },
      ),
    ]);
    const failedUpdate = updates.find((update) => update.status === 'rejected');
    if (failedUpdate) throw failedUpdate.reason;
  }

  function syncMembershipDefaultTab(root, membership) {
    if (!membership) return;
    const defaultTab =
      mapMembershipState(membership).plan_type === 'basic'
        ? 'plans'
        : 'membership';
    // 后续无显式Tab的历史入口也需要当前身份默认值，但不因此重新切换用户正在看的页。
    root.dataset.defaultMembershipTab = defaultTab;
    const navigation = membershipDefaultNavigation.get(root);
    if (!navigation || navigation.settled) return;
    navigation.settled = true;
    if (navigation.userNavigated) return;
    const params = new URLSearchParams(window.location.search);
    const explicitTab = String(params.get('tab') || '').toLowerCase();
    if (explicitTab === 'membership' || explicitTab === 'plans') return;
    const tab = resolveInitialTab(params, defaultTab);
    const selected = root.querySelector(
      '[data-membership-tab][aria-selected="true"]',
    )?.dataset.membershipTab;
    // 本次权威读取已在加载概览，初始化切页不再额外发起一次Tab补读。
    if (selected !== tab) setMembershipTab(root, tab, false, false);
  }

  async function refreshMembership(root, clearRefreshParam) {
    if (!isCustomerLoggedIn()) return false;
    const pendingRefresh = membershipRefreshes.get(root);
    if (pendingRefresh) {
      const result = await pendingRefresh;
      if (result && clearRefreshParam) clearMembershipRefreshParam();
      return result;
    }
    const refresh = (async () => {
      try {
        const membership = await requestMembershipApi('/me');
        updateMembershipOverview(root, membership, {
          publishMembershipState: true,
        });
        syncMembershipDefaultTab(root, membership);
        await loadMembershipOverviewData(root);
        const paywallState = paywallStates.get(root);
        if (paywallState) {
          paywallState.membership = getCurrentMembership();
          renderPaywall(root);
        }
        if (clearRefreshParam) clearMembershipRefreshParam();
        return true;
      } catch (_) {
        return false;
      } finally {
        membershipRefreshes.delete(root);
      }
    })();
    membershipRefreshes.set(root, refresh);
    return refresh;
  }

  function clearMembershipRefreshParam() {
    const url = new URL(window.location.href);
    url.searchParams.delete('refresh_membership');
    window.history.replaceState(
      window.history.state,
      '',
      url.pathname + url.search + url.hash,
    );
  }

  async function refreshMembershipAfterMutation(root) {
    const pendingRefresh = membershipRefreshes.get(root);
    if (pendingRefresh) await pendingRefresh;
    return refreshMembership(root, false);
  }

  function activatePlans(root) {
    try {
      window.jjbCheckoutRuntime?.prepareAnalytics?.();
    } catch {}
    void waitForMembershipPopup().then((popup) => {
      try {
        popup?.prewarm?.();
      } catch {}
    });
    initPaywalls(root);
    const checkoutIntent = getCheckoutIntent(
      new URLSearchParams(window.location.search),
    );
    if (checkoutIntent) continueCheckoutIntent(root, checkoutIntent);
  }

  function setMembershipTab(root, tab, updateUrl, refreshOverview = true) {
    if (updateUrl) {
      const navigation = membershipDefaultNavigation.get(root);
      if (navigation) navigation.userNavigated = true;
    }
    const nextTab = tab === 'plans' ? 'plans' : 'membership';
    const previousTab = root.querySelector(
      '[data-membership-tab][aria-selected="true"]',
    )?.dataset.membershipTab;
    root.querySelectorAll('[data-membership-tab]').forEach((button) => {
      const active = button.dataset.membershipTab === nextTab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      button.setAttribute('tabindex', active ? '0' : '-1');
    });
    root.querySelectorAll('[data-membership-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.membershipPanel !== nextTab;
    });
    if (nextTab === 'plans') activatePlans(root);
    if (
      refreshOverview &&
      nextTab === 'membership' &&
      previousTab === 'plans'
    ) {
      // 进入管理页时可能已有旧读取在途，等待后再同步本次最新状态。
      refreshMembershipAfterMutation(root);
    }
    if (updateUrl) {
      window.history.replaceState(
        window.history.state,
        '',
        getTabUrl(window.location.href, nextTab),
      );
      track('membership_tab_click', {
        entry_page: 'membership',
        tab: nextTab,
      });
      trackMembershipEntryView(root, nextTab);
    }
  }

  function bindMembershipShell(root) {
    if (root.dataset.jjbMembershipShellBound === 'true') return;
    root.dataset.jjbMembershipShellBound = 'true';
    membershipDefaultNavigation.set(root, {
      settled: false,
      userNavigated: false,
    });
    bindMembershipProfile(root);
    root.querySelectorAll('[data-membership-tab]').forEach((button) => {
      button.addEventListener('click', () => {
        setMembershipTab(root, button.dataset.membershipTab, true);
      });
      button.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const tab =
          button.dataset.membershipTab === 'membership'
            ? 'plans'
            : 'membership';
        const next = root.querySelector('[data-membership-tab="' + tab + '"]');
        if (next) {
          next.focus();
          next.click();
        }
      });
    });
    root.querySelectorAll('[data-membership-open-plans]').forEach((button) => {
      button.addEventListener('click', async () => {
        if (button.disabled) return;
        const membership = normalizeMembershipState(getThemeMembershipState());
        if (
          membership.renewalStatus === 'SETUP_PENDING' &&
          membership.renewalSignupId
        ) {
          const popup = await waitForMembershipPopup();
          popup?.openCheckout?.({
            purpose: 'GIFT_RESUME',
            signupId: membership.renewalSignupId,
            scheduledStartsAt: membership.renewalScheduledAt,
          });
          return;
        }
        // 查看套餐保持即时导航；仅已明确欠费的修卡动作需要先核对最新状态。
        if (
          !isCustomerLoggedIn() ||
          !requiresPaymentMethodUpdate(getThemeMembershipState())
        ) {
          setMembershipTab(root, 'plans', true);
          return;
        }
        button.disabled = true;
        try {
          if (isCustomerLoggedIn()) {
            const latest = await requestMembershipApi('/me');
            updateMembershipOverview(root, latest);
            if (requiresPaymentMethodUpdate(latest)) {
              await beginCheckout(
                root,
                'pro',
                'monthly',
                mapMembershipState(latest),
              );
              return;
            }
          }
          setMembershipTab(root, 'plans', true);
        } catch (error) {
          showMembershipRequestError(error);
        } finally {
          button.disabled = false;
        }
      });
    });
    if (isCustomerLoggedIn()) refreshMembership(root, false);

    const cancelDialog = root.querySelector('[data-membership-cancel-dialog]');
    const cancelPlanButton = root.querySelector(
      '[data-membership-cancel-plan]',
    );
    const cancelClose = root.querySelector('[data-membership-cancel-close]');
    const cancelKeep = root.querySelector('[data-membership-cancel-keep]');
    const cancelConfirm = root.querySelector(
      '[data-membership-cancel-confirm]',
    );
    if (cancelDialog) {
      cancelDialog.addEventListener('close', () =>
        setPageScrollLocked(cancelDialog, false),
      );
    }
    if (cancelPlanButton && cancelDialog) {
      cancelPlanButton.addEventListener('click', (event) => {
        if (cancelPlanButton.disabled || cancelDialog.open) return;
        // WebKit 鼠标点击不会自动聚焦按钮，先为原生 dialog 记录真实返回目标。
        event.currentTarget.focus({ preventScroll: true });
        setPageScrollLocked(cancelDialog, true);
        cancelDialog.showModal();
      });
    }
    if (cancelClose) {
      cancelClose.addEventListener('click', () => cancelDialog?.close());
    }
    if (cancelKeep) {
      cancelKeep.addEventListener('click', () => cancelDialog?.close());
    }
    if (cancelConfirm) {
      cancelConfirm.addEventListener('click', () =>
        handleCancelSubscription(root, cancelConfirm, cancelDialog),
      );
    }
    window.addEventListener('popstate', () => {
      const navigation = membershipDefaultNavigation.get(root);
      if (navigation) navigation.userNavigated = true;
      setMembershipTab(
        root,
        resolveInitialTab(
          new URLSearchParams(window.location.search),
          root.dataset.defaultMembershipTab,
        ),
        false,
      );
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refreshMembershipAfterMutation(root);
      else refreshMembership(root, false);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible')
        refreshMembershipAfterMutation(root);
    });
    window.addEventListener('jjb:membership-updated', () =>
      refreshMembershipAfterMutation(root),
    );
  }

  function initPaywalls(targetRoot) {
    const roots = targetRoot
      ? [targetRoot]
      : Array.from(document.querySelectorAll('[data-jjb-membership-paywall]'));
    roots.forEach((root) => {
      if (root.dataset.jjbMembershipBound === 'true') return;
      root.dataset.jjbMembershipBound = 'true';
      const params = new URLSearchParams(window.location.search);
      root.dataset.entryPage =
        params.get('entry_page') || root.dataset.entryPage || 'direct';
      root.dataset.returnUrl =
        params.get('return_url') || root.dataset.returnUrl || '';
      bindPaywallAccordion(root);
      bindCancelSubscriptionTest(root);
      paywallStates.set(root, {
        membership: getCurrentMembership(),
        plans: PLANS,
        loading: false,
      });
      root.querySelectorAll('[data-billing-cycle]').forEach((control) => {
        control.addEventListener('click', () => {
          root.dataset.billingCycle = control.dataset.billingCycle || 'monthly';
          renderPaywall(root);
        });
        control.addEventListener('keydown', (event) => {
          const controls = Array.from(
            root.querySelectorAll('[data-billing-cycle]'),
          );
          const currentIndex = controls.indexOf(control);
          let nextIndex = currentIndex;
          if (event.key === 'ArrowRight') {
            nextIndex = (currentIndex + 1) % controls.length;
          } else if (event.key === 'ArrowLeft') {
            nextIndex = (currentIndex - 1 + controls.length) % controls.length;
          } else if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = controls.length - 1;
          } else {
            return;
          }
          event.preventDefault();
          controls[nextIndex].focus();
          controls[nextIndex].click();
        });
      });
      renderPaywall(root);
    });
  }

  async function initMembershipPage(root) {
    bindComingSoonOverlay(root);
    setPaywallContentBlocked(root, true);
    let enabled = false;
    try {
      const experiment = await getExperiment(VIP_BANNER_EXPERIMENT);
      enabled =
        window.theme?.isVip === true ||
        experiment?.get('enable', false) === true;
    } catch (_) {}
    window.zlog?.recordExperimentGroup?.(
      `${VIP_BANNER_EXPERIMENT}:enable`,
      VIP_BANNER_EXPERIMENT_GROUP_IDS[String(enabled)] ?? 17,
    );
    document.documentElement.classList.add('jjb-membership-experiment-ready');
    if (!enabled) {
      showComingSoon(root);
      return;
    }
    showMembershipShell(root);
    bindMembershipShell(root);
    const params = new URLSearchParams(window.location.search);
    const initialTab = resolveInitialTab(
      params,
      root.dataset.defaultMembershipTab,
    );
    setMembershipTab(root, initialTab, false);
    if (params.get('refresh_membership') === '1') {
      refreshMembership(root, true);
    }
    trackMembershipEntryView(root, initialTab);
  }

  function init() {
    document
      .querySelectorAll('[data-jjb-membership-paywall]')
      .forEach((root) => initMembershipPage(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
